import { useState } from "react";
import { motion } from "framer-motion";
import { X, ZoomIn } from "lucide-react";
import { Dialog, DialogContent } from "./ui/dialog";
import { useApp } from "../context/AppContext";
import { useTranslation } from "../i18n";

const PAGES = [
  {
    url: "https://customer-assets.emergentagent.com/job_q5-ramen/artifacts/z5p4jk9r_carta2.webp",
    label_es: "Ensaladas · Entrantes · Ramen · Arroz",
    label_en: "Salads · Starters · Ramen · Rice",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_q5-ramen/artifacts/454rhruw_carta1.webp",
    label_es: "Tallarines · Carnes · Marisco · Pato · Bebidas",
    label_en: "Noodles · Meats · Seafood · Duck · Drinks",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_q5-ramen/artifacts/5vc7b27n_carta3.webp",
    label_es: "Sushi · Maki · Nigiri · Poke · Sushi al Horno",
    label_en: "Sushi · Maki · Nigiri · Poke · Baked Sushi",
  },
];

export const OfficialMenu = () => {
  const { lang } = useApp();
  const t = useTranslation(lang);
  const [zoom, setZoom] = useState(null);

  return (
    <section id="official-menu" data-testid="official-menu-section" className="relative py-16 md:py-24 lg:py-32 bg-[#0E0E0E]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-12">
          <span className="overline">{t.officialMenu.overline}</span>
          <h2 className="font-serif-q5 text-4xl md:text-5xl mt-3 text-white">{t.officialMenu.title}</h2>
          <p className="text-neutral-400 mt-5 max-w-xl mx-auto">{t.officialMenu.subtitle}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {PAGES.map((p, idx) => (
            <motion.button
              key={idx}
              data-testid={`official-page-${idx}`}
              onClick={() => setZoom(p.url)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              className="group relative overflow-hidden border border-q5 hover:border-gold transition-colors text-left"
            >
              <div className="aspect-square overflow-hidden bg-[#0A0A0A]">
                <img
                  src={p.url}
                  alt={`Carta Q5 ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-transparent to-transparent flex flex-col justify-end p-5">
                <div className="flex items-center justify-between">
                  <p className="text-white text-sm tracking-wide font-serif-q5 text-xl leading-tight">
                    {lang === "es" ? p.label_es : p.label_en}
                  </p>
                  <span className="ml-3 w-10 h-10 border border-gold flex items-center justify-center text-gold flex-shrink-0 group-hover:bg-gold group-hover:text-black transition-colors">
                    <ZoomIn size={16} />
                  </span>
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <Dialog open={!!zoom} onOpenChange={() => setZoom(null)}>
        <DialogContent
          data-testid="official-menu-dialog"
          className="max-w-6xl bg-[#0A0A0A] border-q5 p-2"
        >
          <button
            data-testid="official-menu-close"
            onClick={() => setZoom(null)}
            className="absolute top-3 right-3 z-10 w-10 h-10 bg-black/70 border border-q5 text-white hover:border-gold flex items-center justify-center"
            aria-label={t.officialMenu.close}
          >
            <X size={18} />
          </button>
          {zoom && (
            <img
              src={zoom}
              alt="Carta Q5"
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
